import { useState, type FormEvent } from "react"

import type {
  PlaybookTemplateItem,
  PlaybookTemplateSection,
  PlaybookTemplatesData,
} from "./playbookTemplatesData"

type PlaybookTemplatesProps = {
  authenticityToken: string
  data: PlaybookTemplatesData
}

const templateSections = [
  {
    copy: "Used by the right-hand playbook guidance in the stay notes column.",
    items: [
      { key: "preparing_ahead", label: "Prepare Ahead" },
      { key: "arriving_tomorrow", label: "Arriving Tomorrow" },
      { key: "in_camp", label: "In Camp" },
      { key: "agent_highlights", label: "Highlights" },
      { key: "client_feedback", label: "Guest feedback" },
      { key: "departing_soon", label: "Final camp handover notes" },
      { key: "guide_name", label: "Guide assignment" },
      { key: "waiter_name", label: "Waiter assignment" },
    ],
    title: "Stay notes guidance",
  },
  {
    copy: "Used by the left-hand playbook guidance in the guest profile column.",
    items: [
      { key: "dietaries", label: "About our Guests: Dietary Requirements" },
      { key: "allergies", label: "About our Guests: Allergies" },
      { key: "specific_needs", label: "About our Guests: Specific Needs" },
      { key: "celebrations", label: "Celebrations" },
      { key: "hospitality_food_and_drink", label: "Guest Hospitality: Food & Drink" },
      { key: "guiding_notes", label: "Guest Hospitality: Guiding" },
      { key: "hosting_notes", label: "Guest Hospitality: Hosting" },
      { key: "hospitality_other", label: "Guest Hospitality: Other" },
    ],
    title: "Guest profile guidance",
  },
] satisfies readonly PlaybookTemplateSection[]

const updateEndpoint = "/api/playbook_templates"

export function PlaybookTemplates({ authenticityToken, data }: PlaybookTemplatesProps) {
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [savingKeys, setSavingKeys] = useState<ReadonlySet<string>>(new Set())
  const [templateData, setTemplateData] = useState(data)

  function handleTemplateChange(key: string, value: string) {
    setTemplateData((currentData) => ({
      ...currentData,
      templates: { ...currentData.templates, [key]: value },
    }))
    setErrors((currentErrors) => ({ ...currentErrors, [key]: "" }))
  }

  async function handleTemplateSave(item: PlaybookTemplateItem, value: string) {
    setErrors((currentErrors) => ({ ...currentErrors, [item.key]: "" }))
    setSavingKeys((currentSavingKeys) => new Set(currentSavingKeys).add(item.key))

    try {
      const payload = await updatePlaybookTemplate({
        authenticityToken,
        key: item.key,
        value,
      })
      setTemplateData((currentData) => ({
        ...payload,
        templates: {
          ...currentData.templates,
          [item.key]: payload.templates[item.key] ?? "",
        },
      }))
    } catch {
      setErrors((currentErrors) => ({
        ...currentErrors,
        [item.key]: "Template could not be saved.",
      }))
    } finally {
      setSavingKeys((currentSavingKeys) => {
        const nextSavingKeys = new Set(currentSavingKeys)
        nextSavingKeys.delete(item.key)
        return nextSavingKeys
      })
    }
  }

  return (
    <section className="font-guest [font-synthesis:none]">
      <header className="mb-7">
        <h1 className="text-[34px] font-semibold leading-[35px] text-[#2f2f2f]">
          Playbook guidance templates
        </h1>
        <p className="mt-2 max-w-[760px] text-base leading-6 text-[#7e7e7e]">
          These guidance templates are used by the vacation profile playbook so teams have consistent prompts
          while capturing stay notes and guest profile details.
        </p>
        <p className="mt-4 text-sm font-medium leading-5 text-[#687482]">
          <span className="font-bold text-[#424242]">Last updated by: </span>
          {templateData.last_updated_by ?? "No saved updates yet"}
        </p>
      </header>

      <div className="flex flex-col gap-6">
        {templateSections.map((section) => (
          <TemplateSection
            data={templateData}
            errors={errors}
            key={section.title}
            onTemplateChange={handleTemplateChange}
            onTemplateSave={handleTemplateSave}
            savingKeys={savingKeys}
            section={section}
          />
        ))}
      </div>
    </section>
  )
}

function TemplateSection({
  data,
  errors,
  onTemplateChange,
  onTemplateSave,
  savingKeys,
  section,
}: {
  data: PlaybookTemplatesData
  errors: Record<string, string>
  onTemplateChange: (key: string, value: string) => void
  onTemplateSave: (item: PlaybookTemplateItem, value: string) => Promise<void>
  savingKeys: ReadonlySet<string>
  section: PlaybookTemplateSection
}) {
  return (
    <section className="border-t border-[#d5d5d5] pt-[18px]">
      <div className="mb-4">
        <h2 className="text-2xl font-semibold leading-7 text-[#3f3f3f]">{section.title}</h2>
        <p className="mt-1 text-base leading-6 text-[#7e7e7e]">{section.copy}</p>
      </div>

      <div className="grid gap-[14px]">
        {section.items.map((item) => (
          <TemplateItem
            error={errors[item.key]}
            item={item}
            key={item.key}
            onChange={onTemplateChange}
            onSave={onTemplateSave}
            saved={data.saved_key === item.key}
            saving={savingKeys.has(item.key)}
            value={data.templates[item.key] ?? ""}
          />
        ))}
      </div>
    </section>
  )
}

function TemplateItem({
  error,
  item,
  onChange,
  onSave,
  saved,
  saving,
  value,
}: {
  error?: string
  item: PlaybookTemplateItem
  onChange: (key: string, value: string) => void
  onSave: (item: PlaybookTemplateItem, value: string) => Promise<void>
  saved: boolean
  saving: boolean
  value: string
}) {
  const titleId = `playbook_template_${item.key}_title`
  const statusId = `playbook_template_${item.key}_status`

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    void onSave(item, value)
  }

  return (
    <article
      aria-labelledby={titleId}
      className="grid gap-2.5 rounded border border-[#d5d5d5] bg-white px-[18px] py-4 shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
    >
      <h3 className="text-lg font-semibold leading-6 text-[#454545]" id={titleId}>
        {item.label}
      </h3>

      <form action={updateEndpoint} className="grid gap-2.5" method="post" onSubmit={handleSubmit}>
        <textarea
          aria-label={`${item.label} guidance`}
          aria-describedby={statusId}
          className="min-h-[84px] w-full resize-y rounded border border-[#d5d5d5] bg-white px-3 py-2.5 text-base leading-6 text-[#474747] outline-none focus:border-[#c6c6c6]"
          onChange={(event) => onChange(item.key, event.target.value)}
          value={value}
          name={`playbook_template[${item.key}]`}
          rows={3}
        />
        <div className="flex items-center gap-3">
          <button
            aria-label={`Save ${item.label} template`}
            className="min-w-[88px] rounded border border-[#d5d5d5] bg-white px-4 py-2 text-sm font-bold leading-5 text-[#424242] hover:bg-[#f6f6f6] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#337ab7] focus-visible:ring-offset-2"
            disabled={saving}
            type="submit"
          >
            {saving ? "Saving" : "Save"}
          </button>
          <span
            aria-live="polite"
            className={`text-[13px] font-medium leading-5 ${error ? "text-[#873230]" : "text-[#7e7e7e]"}`}
            id={statusId}
          >
            {error || (saved ? "Saved" : "")}
          </span>
        </div>
      </form>
    </article>
  )
}

async function updatePlaybookTemplate({
  authenticityToken,
  key,
  value,
}: {
  authenticityToken: string
  key: string
  value: string
}) {
  const response = await fetch(updateEndpoint, {
    body: JSON.stringify({ playbook_template: { [key]: value } }),
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": authenticityToken,
    },
    method: "PATCH",
  })

  if (!response.ok) {
    throw new Error(`Playbook template update failed with ${response.status}`)
  }

  return (await response.json()) as PlaybookTemplatesData
}
